import { useEffect } from 'react';
import { navItems, snsLinks } from '@/mocks/site';

interface MenuOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function MenuOverlay({ open, onClose }: MenuOverlayProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <div
      className={`fixed inset-0 z-[70] transition-[opacity,visibility] duration-700 ${
        open ? 'visible opacity-100' : 'invisible opacity-0'
      }`}
      aria-hidden={!open}
    >
      <div className="absolute inset-0 bg-foreground-950" />
      <div
        className={`relative flex h-full flex-col overflow-y-auto px-6 pb-10 pt-5 transition-transform duration-700 md:px-10 ${
          open ? 'translate-y-0' : '-translate-y-4'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="font-heading text-base font-black tracking-[0.06em] text-background-50 md:text-lg">
            H.Y企画
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="メニューを閉じる"
            className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-background-50/25 text-background-50 transition-colors duration-300 hover:border-background-50/60 hover:bg-background-50/10"
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>

        <nav className="mt-10 flex-1 md:mt-14">
          <ul className="space-y-1 md:space-y-2">
            {navItems.map((item, index) => (
              <li
                key={item.href}
                className={`transition-all duration-700 ease-out ${
                  open ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                }`}
                style={{ transitionDelay: open ? `${140 + index * 70}ms` : '0ms' }}
              >
                <a
                  href={item.href}
                  onClick={onClose}
                  className="group flex cursor-pointer items-baseline gap-4 border-b border-background-50/10 py-4 md:gap-6 md:py-5"
                >
                  <span className="mt-2 block h-px w-4 shrink-0 bg-primary-400 transition-all duration-500 group-hover:w-7 md:w-6" />
                  <span className="font-heading text-[22px] font-bold tracking-[0.04em] text-background-50 transition-colors duration-300 group-hover:text-primary-400 md:text-[30px]">
                    {item.jp}
                  </span>
                  <span className="ml-auto text-[10px] en-label text-background-50/40">{item.en}</span>
                  <i className="ri-arrow-right-up-line text-background-50/40 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary-400" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div
          className={`mt-10 flex flex-col gap-6 transition-all duration-700 sm:flex-row sm:items-end sm:justify-between ${
            open ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
          }`}
          style={{ transitionDelay: open ? '620ms' : '0ms' }}
        >
          <div>
            <p className="text-[10px] en-label text-background-50/40">FOLLOW US</p>
            <div className="mt-3 flex items-center gap-4">
              {snsLinks.map((sns) =>
                sns.available ? (
                  <a
                    key={sns.name}
                    href={sns.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={sns.name}
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/20 text-background-50 transition-colors duration-300 hover:border-background-50/60 hover:bg-background-50/10"
                  >
                    <i className={`${sns.icon} text-base`} aria-hidden="true" />
                  </a>
                ) : (
                  <span
                    key={sns.name}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/10 text-background-50/25"
                    aria-label={`${sns.name}（準備中）`}
                  >
                    <i className={`${sns.icon} text-base`} aria-hidden="true" />
                  </span>
                ),
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:items-end">
            <a
              href="#vendor"
              onClick={onClose}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors duration-300 hover:bg-primary-600"
            >
              <i className="ri-store-2-line" aria-hidden="true" />
              出店者募集中
            </a>
            <a
              href="#sponsor"
              onClick={onClose}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-background-50/30 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors duration-300 hover:bg-background-50/10"
            >
              <i className="ri-mail-send-line" aria-hidden="true" />
              協賛募集中
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}